import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PodPanel } from "./PodPanel";
import { InvoicePanel } from "./InvoicePanel";
import {
  ApiError,
  applyLifecycle,
  correctLifecycleTimestamp,
  getAssignmentOptions,
  getLifecycleEvents,
  getLoadTrackingSharing,
  listAssignments,
  startDeviceTracking,
  sendDevicePosition,
  setLoadTrackingSharing,
  stopDeviceTracking,
  updateAssignment,
  type AssignmentOptions,
  type LifecycleEvent,
  type LoadAssignment,
  type TrailerLoadedState,
} from "./api";
import { useAuth } from "./auth";
import { useRealtime } from "./realtime";
import { getOfflineQueueState } from "./offline";
import { LoadTrackingPanel } from "./LoadTrackingPanel";

export function OperationsPage() {
  const auth = useAuth(),
    realtime = useRealtime(),
    company = auth.session?.companies[0],
    companyId = company?.companyId,
    canManage =
      company?.roles.some((r) => r === "owner" || r === "manager") ?? false;
  const [items, setItems] = useState<LoadAssignment[]>([]),
    [options, setOptions] = useState<AssignmentOptions>(),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  async function refresh() {
    if (!companyId) return;
    try {
      setItems(await listAssignments(companyId));
      if (canManage) setOptions(await getAssignmentOptions(companyId));
      setError("");
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, [companyId, realtime.latest?.id]);
  useEffect(
    () => realtime.registerVisibleLoads(items.map((x) => x.loadId)),
    [items],
  );
  if (!companyId) return null;
  return (
    <div className="loads-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">Live work</span>
          <h1>Assignments</h1>
          <p>
            Run each load stop-by-stop, including trailer custody changes and
            automatic waiting-time records.
          </p>
        </div>
      </header>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <p>Loading assignments…</p>
      ) : error ? null : items.length === 0 ? (
        <div className="empty-panel">
          <h2>No assigned work</h2>
        </div>
      ) : (
        <div className="assignment-list">
          {items.map((item) => (
            <AssignmentCard
              key={item.id}
              item={item}
              options={options}
              companyId={companyId}
              refresh={refresh}
              setError={setError}
              highlighted={realtime.latest?.loadId === item.loadId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AssignmentCard({
  item,
  options,
  companyId,
  refresh,
  setError,
  highlighted,
}: {
  item: LoadAssignment;
  options: AssignmentOptions | undefined;
  companyId: string;
  refresh: () => Promise<void>;
  setError: (v: string) => void;
  highlighted: boolean;
}) {
  const [edit, setEdit] = useState(!item.driverUserId && item.canManage),
    [driver, setDriver] = useState(item.driverUserId ?? ""),
    [powered, setPowered] = useState(item.poweredVehicleId),
    [trailer, setTrailer] = useState(item.trailerId ?? ""),
    [busy, setBusy] = useState(false),
    [events, setEvents] = useState<LifecycleEvent[]>([]),
    [queued, setQueued] = useState(false);
  useEffect(() => {
    void getLifecycleEvents(item.loadId, companyId)
      .then(setEvents)
      .catch(() => undefined);
  }, [item.loadId, item.updatedAt]);
  async function run(input: Parameters<typeof applyLifecycle>[1]) {
    setBusy(true);
    setError("");
    try {
      await applyLifecycle(item.loadId, input);
      const queue = await getOfflineQueueState();
      setQueued(queue.pending > 0);
      await refresh();
      if (!queue.pending)
        setEvents(await getLifecycleEvents(item.loadId, companyId));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await updateAssignment(item.loadId, {
        companyId,
        driverUserId: driver,
        poweredVehicleId: powered,
        trailerId: item.movementType === "trailer" ? null : trailer || null,
      });
      setEdit(false);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function correct(event: LifecycleEvent) {
    const value = window.prompt(
      "Correct timestamp (ISO date/time)",
      event.effectiveAt,
    );
    if (!value) return;
    const reason = window.prompt("Reason for correction");
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await correctLifecycleTimestamp(item.loadId, {
        companyId,
        eventId: event.id,
        correctedAt: new Date(value).toISOString(),
        reason,
      });
      await refresh();
      setEvents(await getLifecycleEvents(item.loadId, companyId));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <article
      className={`load-card-live assignment-row${highlighted ? " bid-flash" : ""}`}
    >
      <details className="assignment-accordion">
        <summary>
          <span className="assignment-route">
            <strong>{item.routePostcodes.join(" → ")}</strong>
            <small>{item.description}</small>
          </span>
          <span className="assignment-state">
            <span className="assigned-chip">
              {statusLabel(item.loadStatus)}
            </span>
            <small>{progressLabel(item)}</small>
          </span>
          <span className="assignment-assets">
            <strong>{item.driverEmail ?? "Driver not assigned"}</strong>
            <small>{combinationLabel(item)}</small>
          </span>
          <span className="assignment-poster">
            <small>For</small>
            <strong>{item.posterCompanyName}</strong>
          </span>
          <span className="assignment-expand" aria-hidden="true">
            ⌄
          </span>
        </summary>
        <div className="assignment-detail">
          {queued && (
            <div className="alert alert-warning py-2">
              Action saved on this device and queued for sync.
            </div>
          )}
          {item.warnings.length > 0 && (
            <div className="alert alert-warning py-2">
              {item.warnings.map((w, index) => (
                <div key={w.code + index}>{w.message}</div>
              ))}
            </div>
          )}
          {edit && options ? (
            <AssignmentForm
              item={item}
              options={options}
              driver={driver}
              powered={powered}
              trailer={trailer}
              busy={busy}
              setDriver={setDriver}
              setPowered={setPowered}
              setTrailer={setTrailer}
              cancel={() => setEdit(false)}
              save={save}
            />
          ) : (
            <CombinationSummary item={item} edit={() => setEdit(true)} />
          )}
          <DeviceGps item={item} />
          <LoadTrackingPanel companyId={companyId} loadId={item.loadId} />
          {item.canManage && (
            <TrackingShareControl companyId={companyId} loadId={item.loadId} />
          )}
          {item.loadStatus === "assigned" && item.driverUserId && (
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void run({ companyId, action: "start_journey" })}
            >
              Start journey
            </button>
          )}
          <Stops item={item} companyId={companyId} busy={busy} run={run} />
          {item.legs.length > 0 && <LegHistory item={item} />}{" "}
          {item.loadStatus === "delivered" && (
            <button
              className="btn btn-success"
              disabled={busy}
              onClick={() => void run({ companyId, action: "complete_load" })}
            >
              Complete load and release assets
            </button>
          )}
          <Link
            className="btn btn-sm btn-outline-warning"
            to={"/case/" + item.loadId}
          >
            Cancellation / dispute
          </Link>
          <details>
            <summary>Lifecycle history ({events.length})</summary>
            {events.length === 0 ? (
              <p>No events yet.</p>
            ) : (
              events.map((event) => (
                <p key={event.id}>
                  <strong>{event.eventType.replaceAll("_", " ")}</strong>
                  {event.stopOrder && ` · Stop ${event.stopOrder}`} ·{" "}
                  {new Date(event.effectiveAt).toLocaleString("en-GB")} ·{" "}
                  {event.actorEmail}
                  {event.reason && ` · ${event.reason}`}
                  {event.correctionOfEventId && " · correction"}
                  {item.canManage &&
                    event.eventType !== "timestamp_corrected" && (
                      <button
                        className="btn btn-sm btn-link"
                        disabled={busy}
                        onClick={() => void correct(event)}
                      >
                        Correct time
                      </button>
                    )}
                </p>
              ))
            )}
          </details>
          {(item.loadStatus === "delivered" ||
            item.loadStatus === "completed") && (
            <>
              <PodPanel
                loadId={item.loadId}
                companyId={companyId}
                canManage={item.canManage}
              />
              <InvoicePanel
                loadId={item.loadId}
                companyId={companyId}
                canManage={item.canManage}
              />
            </>
          )}
        </div>
      </details>
    </article>
  );
}

function combinationLabel(item: LoadAssignment) {
  if (item.combinationMode === "external_trailer")
    return `${item.poweredVehicleRegistration} + ${item.currentExternalTrailerIdentifier}`;
  if (item.combinationMode === "fleet_trailer")
    return `${item.poweredVehicleRegistration} + ${item.trailerRegistration}`;
  return `${item.poweredVehicleRegistration} · Solo`;
}

function progressLabel(item: LoadAssignment) {
  if (item.loadStatus === "completed") return "Finished";
  if (item.loadStatus === "delivered") return "Stops finished";
  if (item.loadStatus === "assigned")
    return item.driverUserId ? "Ready to start" : "Needs driver";
  const completed = item.confidentialStops.filter(
    (stop) => stop.lifecycleStatus === "completed",
  ).length;
  const active = item.confidentialStops.find(
    (stop) => stop.lifecycleStatus !== "completed",
  );
  if (!active) return "Stops finished";
  return `${completed}/${item.confidentialStops.length} stops · Stop ${active.order} ${active.lifecycleStatus}`;
}

function TrackingShareControl({
  companyId,
  loadId,
}: {
  companyId: string;
  loadId: string;
}) {
  const [shared, setShared] = useState(true),
    [loaded, setLoaded] = useState(false),
    [busy, setBusy] = useState(false),
    [status, setStatus] = useState("");
  useEffect(() => {
    void getLoadTrackingSharing(companyId, loadId)
      .then((value) => {
        setShared(value.sharedWithPoster);
        setLoaded(true);
      })
      .catch(() => setStatus("Unable to load sharing preference."));
  }, [companyId, loadId]);
  async function change(value: boolean) {
    setBusy(true);
    setStatus("");
    try {
      const saved = await setLoadTrackingSharing(companyId, loadId, value);
      setShared(saved.sharedWithPoster);
      setStatus(
        saved.sharedWithPoster
          ? "The load poster can see this vehicle while tracking is active."
          : "Position hidden from the load poster. Your company can still see it.",
      );
    } catch (e) {
      setStatus(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="tracking-share-control">
      <label>
        <input
          type="checkbox"
          checked={shared}
          disabled={!loaded || busy}
          onChange={(event) => void change(event.target.checked)}
        />
        <span>
          <strong>Share live position with the load poster</strong>
          <small>
            On by default. Turn this off at any time; the poster loses access
            immediately.
          </small>
        </span>
      </label>
      {status && <small>{status}</small>}
    </section>
  );
}

function DeviceGps({ item }: { item: LoadAssignment }) {
  const auth = useAuth(),
    assigned = auth.session?.userId === item.driverUserId,
    eligible = assigned && ["assigned", "in_transit"].includes(item.loadStatus),
    [active, setActive] = useState(false),
    [watchId, setWatchId] = useState<number>(),
    [status, setStatus] = useState("");
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  useEffect(
    () => () => {
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
    },
    [watchId],
  );
  async function start() {
    if (!standalone) {
      setStatus("Install and open the LoadSub PWA before enabling device GPS.");
      return;
    }
    if (!navigator.geolocation) {
      setStatus("Location is not supported on this device.");
      return;
    }
    setStatus("Requesting location permission…");
    try {
      await startDeviceTracking(item.loadId);
      let lastSent = 0;
      const id = navigator.geolocation.watchPosition(
        (position) => {
          const now = Date.now();
          if (now - lastSent < 20000) return;
          lastSent = now;
          void sendDevicePosition(item.loadId, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            recordedAt: new Date(position.timestamp).toISOString(),
            ...(position.coords.speed !== null
              ? { speedKph: Math.max(0, position.coords.speed * 3.6) }
              : {}),
            ...(position.coords.heading !== null
              ? { headingDegrees: position.coords.heading }
              : {}),
            ...(Number.isFinite(position.coords.accuracy)
              ? { accuracyMetres: position.coords.accuracy }
              : {}),
          })
            .then(() =>
              setStatus(
                "Device GPS active · last update " +
                  new Date().toLocaleTimeString("en-GB"),
              ),
            )
            .catch((e) => setStatus(message(e)));
        },
        (error) => {
          setStatus(
            error.code === error.PERMISSION_DENIED
              ? "Location permission was denied. Allow location for LoadSub in your device settings."
              : "Unable to obtain a GPS position. Keep the PWA open and check location services.",
          );
        },
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
      );
      setWatchId(id);
      setActive(true);
    } catch (e) {
      setStatus(message(e));
    }
  }
  async function stop() {
    if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
    setWatchId(undefined);
    setActive(false);
    try {
      await stopDeviceTracking(item.loadId);
      setStatus("Device GPS stopped.");
    } catch (e) {
      setStatus(message(e));
    }
  }
  if (!eligible) return null;
  return (
    <section className="device-gps-panel">
      <div>
        <strong>LoadSub Device GPS</strong>
        <small>
          Shares only this device’s GPS location for this active load. Requires
          the installed LoadSub PWA and location permission; no separate
          tracking system is needed.
        </small>
        <small>
          Keep the PWA open. Android or iOS may pause updates when it is in the
          background.
        </small>
        {status && <span>{status}</span>}
      </div>
      <button
        className={`btn btn-sm ${active ? "btn-outline-danger" : "btn-outline-light"}`}
        type="button"
        onClick={() => void (active ? stop() : start())}
      >
        {active ? "Stop device GPS" : "Start device GPS"}
      </button>
    </section>
  );
}

function CombinationSummary({
  item,
  edit,
}: {
  item: LoadAssignment;
  edit: () => void;
}) {
  const equipment = combinationLabel(item);
  return (
    <div className="assignment-summary assignment-won">
      <strong>{item.driverEmail ?? "Driver not assigned"}</strong>
      <span>{equipment}</span>
      {item.canManage && item.loadStatus === "assigned" && (
        <button className="btn btn-sm btn-outline-light" onClick={edit}>
          Reassign
        </button>
      )}
    </div>
  );
}
function LegHistory({ item }: { item: LoadAssignment }) {
  return (
    <details>
      <summary>Operational combinations ({item.legs.length})</summary>
      {item.legs.map((leg, index) => (
        <p key={index}>
          <strong>
            {leg.fromStopOrder === 0 ? "Start" : `Stop ${leg.fromStopOrder}`} →
            Stop {leg.toStopOrder}
          </strong>{" "}
          · {leg.poweredVehicleRegistration} ·{" "}
          {leg.combinationMode === "solo"
            ? "Solo"
            : (leg.externalTrailerIdentifier ?? leg.trailerRegistration)}
          {leg.trailerLoadedState && ` · ${leg.trailerLoadedState}`}
        </p>
      ))}
    </details>
  );
}

function Stops({
  item,
  companyId,
  busy,
  run,
}: {
  item: LoadAssignment;
  companyId: string;
  busy: boolean;
  run: (i: Parameters<typeof applyLifecycle>[1]) => Promise<void>;
}) {
  return (
    <section className="assigned-stops">
      <strong>Driver work details</strong>
      {item.confidentialStops.map((stop) => (
        <article
          className={`assigned-stop-card lifecycle-${stop.lifecycleStatus}`}
          key={stop.order}
        >
          <div className="assigned-stop-marker">
            <strong>{stop.order}</strong>
          </div>
          <div className="assigned-stop-content">
            <h4>
              {stop.postcode} <small>{stop.lifecycleStatus}</small>
            </h4>
            <dl>
              {(
                [
                  ["Site", stop.siteName],
                  ["Address", stop.address],
                  ["Contact", stop.contactName],
                  ["Telephone", stop.contactTelephone],
                  ["Access", stop.accessInstructions],
                  ["Opening / access times", stop.openingHours],
                  ["Instructions", stop.instructions],
                ] as const
              ).map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd className={value ? "" : "missing-detail"}>
                    {value || "Not provided"}
                  </dd>
                </div>
              ))}
            </dl>
            {stop.operations.length > 0 && (
              <div className="trailer-operation-list">
                <strong>Trailer actions</strong>
                {stop.operations.map((operation, index) => (
                  <p key={index}>
                    {operation.action.replaceAll("_", " ")}
                    {operation.trailerIdentifier &&
                      ` · ${operation.trailerIdentifier}`}
                    {operation.loadedState && ` · ${operation.loadedState}`}
                    {operation.completedAt && (
                      <span className="status-chip status-active">
                        Recorded
                      </span>
                    )}
                  </p>
                ))}
              </div>
            )}
            {stop.lifecycleStatus === "pending" &&
              item.loadStatus === "in_transit" && (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={busy}
                  onClick={() =>
                    void run({
                      companyId,
                      action: "arrive",
                      stopOrder: stop.order,
                    })
                  }
                >
                  Arrived — start waiting
                </button>
              )}
            {stop.lifecycleStatus === "arrived" && (
              <OutcomeForm
                companyId={companyId}
                stop={stop}
                busy={busy}
                run={run}
                started={stop.waitingStartedAt}
              />
            )}{" "}
            {stop.outcomeReason && (
              <p>
                <strong>Outcome:</strong> {stop.outcomeReason}
              </p>
            )}
            {stop.evidenceNotes && (
              <p>
                <strong>Evidence:</strong> {stop.evidenceNotes}
              </p>
            )}
          </div>
        </article>
      ))}
    </section>
  );
}

function OutcomeForm({
  companyId,
  stop,
  busy,
  run,
  started,
}: {
  companyId: string;
  stop: LoadAssignment["confidentialStops"][number];
  busy: boolean;
  run: (i: Parameters<typeof applyLifecycle>[1]) => Promise<void>;
  started: string | null;
}) {
  const [outcome, setOutcome] = useState<"completed" | "partial" | "failed">(
      "completed",
    ),
    [reason, setReason] = useState(""),
    [evidence, setEvidence] = useState(""),
    [operations, setOperations] = useState(() =>
      stop.operations.map((operation, index) => ({
        operationOrder: operation.operationOrder ?? index + 1,
        actualTrailerIdentifier:
          operation.actualTrailerIdentifier ??
          operation.trailerIdentifier ??
          "",
        actualLoadedState:
          operation.actualLoadedState ??
          operation.loadedState ??
          ("unknown" as TrailerLoadedState),
        sealNumber: operation.sealNumber ?? "",
        conditionNotes: operation.conditionNotes ?? "",
      })),
    );
  function update(index: number, key: string, value: string) {
    setOperations((current) =>
      current.map((operation, item) =>
        item === index ? { ...operation, [key]: value } : operation,
      ),
    );
  }
  return (
    <form
      className="bid-form"
      onSubmit={(e) => {
        e.preventDefault();
        void run({
          companyId,
          action: "finish_stop",
          stopOrder: stop.order,
          outcome,
          ...(reason ? { reason } : {}),
          ...(evidence ? { evidenceNotes: evidence } : {}),
          ...(outcome === "completed" && operations.length
            ? {
                trailerOperations: operations.map((operation) => ({
                  operationOrder: operation.operationOrder,
                  ...(operation.actualTrailerIdentifier
                    ? {
                        actualTrailerIdentifier:
                          operation.actualTrailerIdentifier,
                      }
                    : {}),
                  actualLoadedState: operation.actualLoadedState,
                  ...(operation.sealNumber
                    ? { sealNumber: operation.sealNumber }
                    : {}),
                  ...(operation.conditionNotes
                    ? { conditionNotes: operation.conditionNotes }
                    : {}),
                })),
              }
            : {}),
        });
      }}
    >
      <strong>
        Waiting: <Elapsed from={started} />
      </strong>
      {outcome === "completed" && stop.operations.length > 0 && (
        <div className="trailer-operation-completion">
          {stop.operations.map((planned, index) => (
            <fieldset key={index}>
              <legend>{planned.action.replaceAll("_", " ")}</legend>
              <div className="bid-fields">
                {[
                  "collect_trailer",
                  "swap_trailer",
                  "couple",
                  "drop_trailer",
                ].includes(planned.action) && (
                  <label>
                    <span>
                      Actual trailer number{" "}
                      {planned.trailerIdentifier ? "" : "(required)"}
                    </span>
                    <input
                      className="form-control text-uppercase"
                      required={
                        !planned.trailerIdentifier &&
                        ["collect_trailer", "swap_trailer", "couple"].includes(
                          planned.action,
                        )
                      }
                      value={operations[index]?.actualTrailerIdentifier ?? ""}
                      onChange={(e) =>
                        update(index, "actualTrailerIdentifier", e.target.value)
                      }
                    />
                  </label>
                )}
                <label>
                  <span>Loaded state</span>
                  <select
                    className="form-select"
                    value={operations[index]?.actualLoadedState ?? "unknown"}
                    onChange={(e) =>
                      update(index, "actualLoadedState", e.target.value)
                    }
                  >
                    <option value="unknown">Unknown</option>
                    <option value="loaded">Loaded</option>
                    <option value="empty">Empty</option>
                  </select>
                </label>
                <label>
                  <span>
                    Seal number <small>optional</small>
                  </span>
                  <input
                    className="form-control"
                    value={operations[index]?.sealNumber ?? ""}
                    onChange={(e) =>
                      update(index, "sealNumber", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Condition / damage notes</span>
                  <input
                    className="form-control"
                    value={operations[index]?.conditionNotes ?? ""}
                    onChange={(e) =>
                      update(index, "conditionNotes", e.target.value)
                    }
                  />
                </label>
              </div>
            </fieldset>
          ))}
        </div>
      )}
      <div className="bid-fields">
        <label>
          <span>Outcome</span>
          <select
            className="form-select"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as typeof outcome)}
          >
            <option value="completed">Completed</option>
            <option value="partial">Partially completed</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label>
          <span>Reason {outcome !== "completed" && "(required)"}</span>
          <input
            className="form-control"
            required={outcome !== "completed"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <label>
          <span>Evidence notes</span>
          <input
            className="form-control"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
          />
        </label>
      </div>
      <button className="btn btn-sm btn-success" disabled={busy}>
        Record outcome and depart
      </button>
    </form>
  );
}

function Elapsed({ from }: { from: string | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!from) return <>00:00</>;
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(from).getTime()) / 1000),
  );
  return (
    <>
      {Math.floor(seconds / 3600)
        .toString()
        .padStart(2, "0")}
      :
      {Math.floor((seconds % 3600) / 60)
        .toString()
        .padStart(2, "0")}
      :{(seconds % 60).toString().padStart(2, "0")}
    </>
  );
}

function AssignmentForm(p: {
  item: LoadAssignment;
  options: AssignmentOptions;
  driver: string;
  powered: string;
  trailer: string;
  busy: boolean;
  setDriver: (v: string) => void;
  setPowered: (v: string) => void;
  setTrailer: (v: string) => void;
  cancel: () => void;
  save: (e: FormEvent) => void;
}) {
  const powered =
    p.item.movementType === "trailer"
      ? p.options.poweredVehicles.filter((x) =>
          p.item.trailerCouplingType === "semi"
            ? x.couplingType === "fifth_wheel" || x.couplingType === "both"
            : x.couplingType === "drawbar" || x.couplingType === "both",
        )
      : p.options.poweredVehicles;
  return (
    <form className="bid-form" onSubmit={p.save}>
      <div className="bid-fields">
        <label>
          <span>Driver</span>
          <select
            required
            className="form-select"
            value={p.driver}
            onChange={(e) => p.setDriver(e.target.value)}
          >
            <option value="">Select driver</option>
            {p.options.drivers.map((x) => (
              <option value={x.id} key={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Powered vehicle</span>
          <select
            required
            className="form-select"
            value={p.powered}
            onChange={(e) => p.setPowered(e.target.value)}
          >
            <option value="">Select compatible vehicle</option>
            {powered.map((x) => (
              <option value={x.id} key={x.id}>
                {x.label} · {x.status}
              </option>
            ))}
          </select>
        </label>
        {p.item.movementType === "goods" && (
          <label>
            <span>Carrier fleet trailer</span>
            <select
              className="form-select"
              value={p.trailer}
              onChange={(e) => p.setTrailer(e.target.value)}
            >
              <option value="">Solo / no fleet trailer</option>
              {p.options.trailers.map((x) => (
                <option value={x.id} key={x.id}>
                  {x.label} · {x.status}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {p.item.movementType === "trailer" && (
        <p className="text-secondary small">
          The customer's trailers are recorded at stops and are not added to
          your fleet.
        </p>
      )}
      <button
        className="btn btn-sm btn-primary"
        disabled={p.busy || !p.driver || !p.powered}
      >
        Save assignment
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-light"
        onClick={p.cancel}
      >
        Cancel
      </button>
    </form>
  );
}

const statusLabel = (s: LoadAssignment["loadStatus"]) =>
  ({
    assigned: "Assigned",
    in_transit: "In progress",
    delivered: "Stops finished",
    completed: "Completed",
  })[s];
const message = (e: unknown) =>
  e instanceof ApiError ? e.message : "Unable to update the load.";
